import i18n from "@/shared/config/i18n";

const AVATAR_COLORS = [
  ["bg-blue-50", "text-blue-600"],
  ["bg-violet-50", "text-violet-600"],
  ["bg-green-50", "text-green-600"],
  ["bg-orange-50", "text-orange-600"],
  ["bg-pink-50", "text-pink-600"],
  ["bg-teal-50", "text-teal-600"],
  ["bg-gray-100", "text-gray-700"],
];

const FACE_SKINS = [
  "#f4c89a",
  "#e8b087",
  "#d79877",
  "#c98a5f",
  "#9c6a46",
  "#7a4e32",
];
const FACE_HAIR = [
  "#1e1e1e",
  "#3b2a1e",
  "#6b4a2d",
  "#a67347",
  "#d8b58a",
  "#9c9c9c",
];
const FACE_BG = [
  "#2563eb", // blue-600
  "#10b981", // emerald-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#14b8a6", // teal-500
  "#f59e0b", // amber-500
  "#475569", // slate-600
];

export function faceSeedFor(id, gender = null) {
  const value = String(id || "");
  let h = 5381;
  for (let i = 0; i < value.length; i++) {
    h = (h << 5) + h + value.charCodeAt(i);
  }
  const s = Math.abs(h);
  const isFemale = gender === "female";
  return {
    skin: FACE_SKINS[s % FACE_SKINS.length],
    hair: FACE_HAIR[(s >> 3) % FACE_HAIR.length],
    bg: FACE_BG[(s >> 6) % FACE_BG.length],
    eyeDist: ((s >> 9) % 3) + 2,
    smile: isFemale ? true : (s >> 11) % 2 === 1,
    glasses: (s >> 13) % 4 === 0,
    beard: isFemale ? false : (s >> 15) % 3 === 0,
    longHair: isFemale,
  };
}

export function detectGenderFromIin(iin) {
  const digits = String(iin || "").replace(/\D/g, "");
  if (digits.length !== 12) return null;
  const marker = Number(digits[6]);
  if (!marker) return null;
  return marker % 2 === 1 ? "male" : "female";
}

export function darkenHex(hex, amount = 0.3) {
  const match = String(hex || "").match(/^#?([0-9a-f]{6})$/i);
  if (!match) return hex;
  const n = parseInt(match[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 0xff) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

const AVATAR_PALETTE = [
  ["#9B7EDC", "#6A4FB0"], // violet
  ["#6E9AE0", "#3E68B8"], // blue
  ["#5FC29A", "#2E8A6E"], // emerald
  ["#E08AA8", "#B0567A"], // rosepink
  ["#D06E72", "#9A3E42"], // rosered
  ["#6AC2D0", "#2E8A9A"], // cyan
  ["#A888D6", "#6E54A8"], // plum
  ["#72B880", "#3E8050"], // green
  ["#7E8ED6", "#4E5EA8"], // indigo
  ["#D07070", "#9A3E3E"], // terracotta
  ["#5FB8AE", "#2E8078"], // teal
];

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function nameToGradient(name) {
  const [from, to] =
    AVATAR_PALETTE[hashString(String(name || "")) % AVATAR_PALETTE.length];
  return `linear-gradient(180deg, ${from} 0%, ${to} 100%)`;
}

export function hashColor(seed) {
  if (!seed) return AVATAR_COLORS[0];
  const s = String(seed);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export function initialsOf(name) {
  if (!name) return "";
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export function formatDate(value) {
  if (!value) return i18n.global.t("Не указано");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString();
}

export function formatDateTime(value) {
  if (!value) return i18n.global.t("Не указано");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

export function formatShortTime(value) {
  if (!value) return i18n.global.t("Не указано");
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export function normalizeScore(score) {
  if (typeof score !== "number" || Number.isNaN(score)) return null;
  if (score > 1) return score / 100;
  return score;
}

export function fullName(user) {
  if (!user) return "";
  const first = user.first_name || user.firstName || "";
  const last = user.last_name || user.lastName || "";
  const name = [last, first].filter(Boolean).join(" ").trim();
  return name || user.name || user.identifier || "";
}
